<template>
  <v-card outlined class="cvc-migration-header pa-3" :class="{ 'grey lighten-4': refresh.stale }">
    <div class="d-flex flex-wrap align-center">
      <v-tooltip bottom>
        <template v-slot:activator="{ on }">
          <span class="title font-weight-bold mr-2" v-on="on">{{ name }}</span>
        </template>
        <span>Usher CVC id {{ usherId }}</span>
      </v-tooltip>
      <span class="grey--text text--darken-1 mr-4">{{ identifier }}</span>
      <v-tooltip bottom>
        <template v-slot:activator="{ on }">
          <span class="mr-4" v-on="on">POI {{ poiState }}</span>
        </template>
        <span>Target state comes from the POI, not the router</span>
      </v-tooltip>
      <v-tooltip bottom :disabled="!activated">
        <template v-slot:activator="{ on }">
          <span class="mr-4" v-on="on">{{ routeText }}</span>
        </template>
        <span>now on {{ targetHostname }} (local termination)</span>
      </v-tooltip>
      <span v-if="preflight && preflight.localNetwork" class="mr-4">
        LOCAL {{ preflight.localNetwork.bridgeDomain }} / outer {{ preflight.localNetwork.outerVlan }}
      </span>
      <span v-else-if="preflight" class="mr-4 red--text font-weight-bold">No LOCAL network</span>
      <v-spacer />
      <v-tooltip v-if="showRollback" bottom>
        <template v-slot:activator="{ on }">
          <v-btn small outlined color="error" class="mr-3" v-on="on" @click="$emit('rollback')">Rollback...</v-btn>
        </template>
        <span>Undo what this migration has done so far</span>
      </v-tooltip>
      <v-tooltip bottom>
        <template v-slot:activator="{ on }">
          <v-chip :color="phase.color" :dark="phase.color !== 'amber'" small v-on="on">{{ phase.label }}</v-chip>
        </template>
        <span>{{ phase.meaning }}</span>
      </v-tooltip>
    </div>

    <div class="mt-2">
      <span class="font-weight-medium mr-2">Window</span>
      <template v-if="windowText">
        <span class="mr-2">{{ windowText }}</span>
        <span :class="windowClass">{{ windowRelative.text }}</span>
      </template>
      <span v-else class="grey--text text--darken-1">No window set</span>
    </div>

    <div class="mt-1 d-flex flex-wrap align-center">
      <span class="mr-4" :class="{ 'grey--text text--darken-1': !record }">{{ recordText }}</span>
      <span class="mr-2" :class="{ 'grey--text text--darken-2 font-weight-medium': refresh.stale }">{{ refresh.text }}</span>
      <v-btn v-if="refresh.stale" x-small text color="primary" @click="$emit('retry')">Retry</v-btn>
      <v-chip v-if="changedAt" x-small color="info" dark class="ml-2">Updated by another session at {{ changedAt }}</v-chip>
    </div>

    <div class="mt-2">
      <v-chip v-if="phaseKey === 'cancelled'" small>Cancelled</v-chip>
      <template v-else>
        <v-chip
          v-for="step in strip"
          :key="step.label"
          small
          class="mr-1 mb-1"
          :color="stepColor(step)"
          :outlined="step.state === 'todo'"
          :dark="step.state === 'current' && step.color !== 'amber'"
        >{{ step.label }}</v-chip>
      </template>
    </div>
  </v-card>
</template>

<script>
import { formatDual, relativeWindow } from "@/utils/zonedTime";
import { phaseInfo, phaseStrip, windowRangeText } from "@/utils/cvcMigrationPage";
import { rollbackPoint, showRollbackButton } from "@/utils/cvcMigrationRollback";

// The sticky header: which CVC, where it moves, the phase and the window. It
// reads the Preflight answer; before the first answer (or when Preflight
// failed) it falls back to the picker's list item.
export default {
  name: "CvcMigrationHeader",
  props: {
    preflight: { type: Object, default: null },
    // The picker's list item (V1), used while there is no Preflight answer.
    cvc: { type: Object, default: null },
    hosts: { type: Array, default: () => [] },
    // { stale, text } from refreshStatus
    refresh: { type: Object, required: true },
    // "10:21 SYD" when another session changed the record, else ""
    changedAt: { type: String, default: "" },
    now: { type: Date, required: true },
  },
  computed: {
    record () {
      return this.preflight ? this.preflight.record : null;
    },
    name () {
      if (this.preflight) return this.preflight.cvcName;
      return this.cvc ? this.cvc.cvcName || this.cvc.cvcId : "";
    },
    identifier () {
      if (this.preflight) return this.preflight.cvcIdentifier;
      return this.cvc ? this.cvc.cvcId : "";
    },
    usherId () {
      if (this.preflight) return this.preflight.cvcId;
      return this.cvc ? this.cvc.id : "";
    },
    poiState () {
      if (this.preflight) return this.preflight.poiState;
      return this.cvc ? this.cvc.poiState : "";
    },
    sourceHostname () {
      if (this.preflight) return this.preflight.sourceHostname;
      const host = this.cvc && this.hosts.find(h => h.id === this.cvc.networkHostId);
      return host ? host.hostname : "";
    },
    targetHostname () {
      return this.preflight ? this.preflight.targetHostname : "";
    },
    phaseKey () {
      return this.preflight && this.preflight.steps ? this.preflight.steps.phase : "none";
    },
    phase () {
      return phaseInfo(this.phaseKey);
    },
    strip () {
      return phaseStrip(this.phaseKey);
    },
    activated () {
      return ["activated", "migrated", "removed"].includes(this.phaseKey);
    },
    showRollback () {
      return showRollbackButton(rollbackPoint(this.preflight, null));
    },
    routeText () {
      if (this.activated) return `now on ${this.targetHostname} (local termination)`;
      return `${this.sourceHostname} -> ${this.targetHostname || "(choose target)"}`;
    },
    windowText () {
      return this.record ? windowRangeText(this.record.cutoverWindowStart, this.record.cutoverWindowEnd, this.poiState) : "";
    },
    windowRelative () {
      return relativeWindow(this.record.cutoverWindowStart, this.record.cutoverWindowEnd, this.now);
    },
    windowClass () {
      if (this.windowRelative.kind === "open") return "green--text text--darken-2 font-weight-medium";
      if (this.windowRelative.kind === "ended") return "amber--text text--darken-3 font-weight-medium";
      return "";
    },
    recordText () {
      if (!this.record) return "No migration record yet";
      return `Record #${this.record.id} started by ${this.record.createdBy} ${formatDual(this.record.createdAt, this.poiState)}`;
    },
  },
  methods: {
    stepColor (step) {
      if (step.state === "todo") return "";
      return step.state === "done" ? "grey lighten-2" : step.color;
    },
  },
};
</script>
